import { useState } from "react";
import { Box, Collapse, IconButton, List, ListItem, Typography, useTheme } from "@mui/material";
import CustomIcon from "components/CustomIcon";

import { getAbilityIcon } from "utils";
import icons from "enums/icons";

import { ICReportPerson } from "store/slice/Project/icDashboard/icDashboard.types";
import { camelCase, startCase } from "lodash";

export interface ITeamDataCardProps {
    teamData: ICReportPerson[] | null;
    companyName: string;
    disabled: boolean;
}

const TeamDataCard = ({ teamData, companyName, disabled }: ITeamDataCardProps) => {
    const theme = useTheme();

    const [isTeamExpanded, setIsTeamExpanded] = useState<boolean>(false);

    const handleTeamExpand = () => {
        if (disabled) return;
        setIsTeamExpanded(!isTeamExpanded);
    };

    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "flex-start",
                height: "100%",
                backgroundColor: theme.palette.background.grey,
                p: 1,
                borderRadius: 2,
            }}
        >
            <Box display="flex" alignItems="flex-start" flexDirection="column-reverse">
                <Typography variant="h5" sx={{ fontWeight: 600, writingMode: "sideways-lr", marginTop: 1 }}>
                    {companyName} Leadership Team
                </Typography>
                <IconButton onClick={handleTeamExpand}>
                    <CustomIcon
                        icon={isTeamExpanded ? icons.circledChevronLeftThin : icons.circledChevronRightThin}
                        sx={{ color: theme.palette.primary.main }}
                    />
                </IconButton>
            </Box>
            <Collapse
                orientation="horizontal"
                in={isTeamExpanded}
                timeout={200}
                easing={{ enter: "ease-out", exit: "ease-in" }}
                sx={{
                    height: "100%",
                    "& .MuiCollapse-wrapper": { height: "100%" },
                    "& .MuiCollapse-wrapperInner": { height: "100%", width: 280 },
                }}
            >
                <List
                    sx={{
                        width: 280,
                        ml: 1,
                        overflowY: "auto",
                        overflowX: "hidden",
                        height: "100%",
                    }}
                >
                    {teamData?.map((person) => {
                        const overallScoreColor = person.ability_scores["overall"]?.RAG?.colour ?? null;
                        return (
                            <ListItem key={person.member_id} disablePadding sx={{ mb: "16px" }}>
                                <Box display="flex" alignItems="center" sx={{ width: "100%" }}>
                                    <CustomIcon icon={getAbilityIcon(overallScoreColor)} />
                                    <Box ml={1} sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={700}>
                                            {startCase(camelCase(person.member_name ?? ""))}
                                        </Typography>
                                        <Typography variant="body2">{person.job_title ?? ""}</Typography>
                                    </Box>
                                </Box>
                            </ListItem>
                        );
                    })}
                </List>
            </Collapse>
        </Box>
    );
};

export default TeamDataCard;
